/**
 * Orchestrates git sync for a workspace's YAML mirror folder.
 *
 * Flow: the mirror folder (AppLocalData/workspace-mirror/{id}) is continuously
 * written by workspaceMirror. Here we turn it into a git repo, attach a remote
 * on the workspace's git provider (./git/providers — GitHub today), and
 * push/pull via the Rust git_sync (libgit2) commands. Conflicts are returned
 * to the caller to drive the keep-local / keep-remote modal.
 *
 * Reading remote changes back into the app reuses loadGitSnapshot() — the mirror
 * has the identical YAML layout as a git-storage workspace.
 */
import { invoke } from '@tauri-apps/api/core';
import { mirrorAbsDir, writeMirror, readMirror } from './workspaceMirror';
import { loadGitSnapshot } from './gitWorkspaceStore';
import { scanForSecrets } from './git/secretScan.js';
import { commitMessageFor } from './git/mirrorChanges';
import { listAllYaml, readTextSafe } from './gitFileWriter.js';
import { flushWorkspaceToDisk } from './gitFlush';
import { syncEngineEnabled } from '../utils/featureFlags';
import { getProvider, providerOf, normalizeGitLink } from './git/providers';
import { catalogFromList } from './git/repoCatalog';
import { DEFAULT_BRANCH, branchOfLink, requireToken, credArgs, authorFor, statusToken } from './git/gitAuth';
import { APP_NAME } from '../utils/appInfo';

/**
 * Phase 4: flag-enrolled workspaces flush the mirror from SQLite (flat
 * yamlWorkspace layout, gitFlush.js); non-enrolled keep the legacy nested
 * writeMirror path untouched.
 */
/**
 * Tracked workspace files as they are on disk right before a commit — what
 * `git_commit_all` would stage (managed *.yaml + apiboo.yaml; `*.local.yaml`
 * is git-ignored and skipped by the scanner).
 */
export async function scanMirrorForSecrets(dir) {
  const fs = await import('@tauri-apps/plugin-fs');
  const files = {};
  // Every tracked-able YAML file, both layouts (the nested mirror keeps its
  // collection folders at the top level — they were not scanned before 02.10.2026).
  for (const rel of new Set(['apiboo.yaml', ...(await listAllYaml(fs, dir))])) {
    const text = await readTextSafe(fs, `${dir}/${rel}`);
    if (text != null) files[rel] = text;
  }
  return scanForSecrets(files);
}

export async function refreshMirror(workspace, snapshot) {
  if (syncEngineEnabled(workspace)) {
    await flushWorkspaceToDisk(workspace);
    return;
  }
  if (snapshot) await writeMirror(workspace, snapshot);
}

/** True when a repo dir uses the flat yamlWorkspace layout (collections/…). */
async function isFlatLayoutDir(dir) {
  try {
    const fs = await import('@tauri-apps/plugin-fs');
    if (await fs.exists(`${dir}/collections`)) return true;
    // No collections dir: nested layout keeps each collection as an arbitrary
    // top-level directory holding collection.yaml. Zero-collection repos look
    // identical in both layouts — the flat reader handles those fine.
    const reserved = new Set(['.git', 'variables', 'environments', 'schemas']);
    const entries = await fs.readDir(dir);
    for (const entry of entries) {
      if (!entry?.isDirectory || !entry.name || reserved.has(entry.name)) continue;
      if (await fs.exists(`${dir}/${entry.name}/collection.yaml`)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Layout-aware read of a mirror/clone dir back into a snapshot. */
async function readRepoSnapshot(workspaceId) {
  const dir = (await mirrorAbsDir(workspaceId)).replace(/\\/g, '/');
  if (await isFlatLayoutDir(dir)) return loadGitSnapshot(dir);
  return readMirror(workspaceId);
}

function branchOf(workspace) {
  return branchOfLink(workspace?.git);
}

/**
 * A workspace tracked only on this device (Track on this device) has no
 * remote: pushing, pulling or resolving against one is a caller bug, never
 * a silent no-op. Its saves go through ./git/localGitSync.js.
 */
function refuseLocalOnly(workspace) {
  if (workspace?.git?.provider === 'local') {
    const err = new Error('LOCAL_ONLY');
    err.code = 'LOCAL_ONLY';
    throw err;
  }
}

/**
 * Connect a workspace's mirror to a repo and do the initial push.
 * @returns {{status:'ok'|'diverged'|'secrets', git?:object, fresh?:boolean}}
 *   `fresh` (on 'diverged'): this device had no history before the connect.
 */
export async function connectRepo(
  workspace,
  {
    owner,
    repo,
    createIfMissing = true,
    isPrivate = true,
    snapshot,
    allowSecrets = false,
    provider: providerId,
    accountId,
    url: remoteUrl, // "Other git server": the URL as pasted
    vaultItemId,
    vaultItemName,
  } = {}
) {
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId, url: remoteUrl, vaultItemId, vaultItemName };
  const token = await requireToken(provider, ctx);
  if (!owner || !repo) throw new Error('Repository owner and name are required');

  const dir = await mirrorAbsDir(workspace.id);
  await refreshMirror(workspace, snapshot);

  if (createIfMissing && provider.abilities.createRepo) {
    await provider.ensureRepoExists(token, owner, repo, isPrivate, ctx);
  }

  const author = await authorFor(provider, ctx);
  const url = await provider.repoUrl(owner, repo, ctx);
  const git = normalizeGitLink(
    {
      owner,
      repo,
      branch: DEFAULT_BRANCH,
      url,
      provider: provider.id,
      ...(vaultItemId ? { vaultItemId, vaultItemName: vaultItemName || '' } : {}),
    },
    { accountId: accountId || author.authorName || undefined }
  );

  // Card 5: nothing that looks like a credential is committed unless the
  // user said so after seeing exactly where (file:line, never the value).
  if (!allowSecrets) {
    const findings = await scanMirrorForSecrets(dir);
    if (findings.length > 0) return { status: 'secrets', findings, git };
  }

  await invoke('git_init_or_open', { repoPath: dir, branch: DEFAULT_BRANCH });
  await invoke('git_set_remote', { repoPath: dir, name: 'origin', url });
  // Card 01fee74a: no history on this device yet → a conflict with what the
  // remote holds is a JOIN, resolved on top of the remote's (./git/joinRemote.js).
  const fresh = !(await invoke('git_status', { repoPath: dir, remote: 'origin', branch: DEFAULT_BRANCH }).catch(() => null))?.headOid;
  await invoke('git_commit_all', {
    repoPath: dir,
    // Readable history (set 7d): "3 requests, 1 environment", not boilerplate.
    message: await commitMessageFor(dir.replace(/\\/g, '/'), `Sync workspace from ${APP_NAME}`),
    ...author,
  });

  const push = await invoke('git_push', {
    repoPath: dir,
    ...credArgs(provider, token, git),
    remote: 'origin',
    branch: DEFAULT_BRANCH,
  });

  // Flag the repo so it shows under "My workspaces" in the picker.
  if (provider.abilities.tagWorkspace) provider.tagWorkspace(token, owner, repo, git);

  if (push.status === 'diverged') {
    return { status: 'diverged', git, fresh };
  }
  return { status: 'ok', git };
}

/**
 * Commit + push the latest workspace state. On divergence, attempts a merge;
 * returns conflicts for the UI when they can't be auto-merged.
 * @returns {{status:'ok'|'conflicts'|'diverged'|'secrets', conflicts?:string[], git:object}}
 */
export async function pushWorkspace(workspace, snapshot, { allowSecrets = false } = {}) {
  if (!workspace?.git) throw new Error('NOT_CONNECTED');
  refuseLocalOnly(workspace);
  const provider = providerOf(workspace);
  const token = await requireToken(provider, workspace.git);
  const cred = credArgs(provider, token, workspace.git);

  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOf(workspace);
  await refreshMirror(workspace, snapshot);

  if (!allowSecrets) {
    const findings = await scanMirrorForSecrets(dir);
    if (findings.length > 0) return { status: 'secrets', findings, git: workspace.git };
  }

  const author = await authorFor(provider, workspace.git);

  await invoke('git_commit_all', {
    repoPath: dir,
    message: await commitMessageFor(dir.replace(/\\/g, '/'), `Update workspace from ${APP_NAME}`),
    ...author,
  });

  // Backfill the workspace topic for repos connected before tagging existed.
  if (workspace.git.owner && workspace.git.repo && provider.abilities.tagWorkspace) {
    provider.tagWorkspace(token, workspace.git.owner, workspace.git.repo, workspace.git);
  }

  let push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
  if (push.status !== 'diverged') {
    return { status: 'ok', git: workspace.git };
  }

  // Remote moved ahead — fetch + merge, then push again.
  const merge = await invoke('git_merge_remote', {
    repoPath: dir,
    ...cred,
    remote: 'origin',
    branch,
    ...author,
  });
  if (merge.status === 'conflicts') {
    return { status: 'conflicts', conflicts: merge.conflicts, git: workspace.git };
  }

  push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
  return { status: push.status === 'ok' ? 'ok' : 'diverged', git: workspace.git };
}

/**
 * Pull (fetch + merge) remote changes into the mirror.
 * @returns {{status:'up_to_date'|'fast_forward'|'merged'|'conflicts', conflicts?:string[]}}
 */
export async function pullWorkspace(workspace) {
  if (!workspace?.git) throw new Error('NOT_CONNECTED');
  refuseLocalOnly(workspace);
  const provider = providerOf(workspace);
  const token = await requireToken(provider, workspace.git);

  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOf(workspace);

  return invoke('git_merge_remote', {
    repoPath: dir,
    ...credArgs(provider, token, workspace.git),
    remote: 'origin',
    branch,
    ...(await authorFor(provider, workspace.git)),
  });
}

/**
 * Apply per-file conflict resolutions (keep local / keep remote), commit, push.
 * @param {{path:string, side:'local'|'remote'}[]} resolutions
 */
export async function resolveConflicts(workspace, resolutions) {
  if (!workspace?.git) throw new Error('NOT_CONNECTED');
  refuseLocalOnly(workspace);
  const provider = providerOf(workspace);
  const token = await requireToken(provider, workspace.git);

  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOf(workspace);

  await invoke('git_resolve', {
    repoPath: dir,
    resolutions,
    remote: 'origin',
    branch,
    ...(await authorFor(provider, workspace.git)),
  });

  const push = await invoke('git_push', {
    repoPath: dir,
    ...credArgs(provider, token, workspace.git),
    remote: 'origin',
    branch,
  });
  return { status: push.status };
}

/** Fetch (remote links only) + report ahead/behind/local-change status for the badge. */
export async function gitStatus(workspace) {
  if (!workspace?.git) return { initialized: false, hasRemote: false };
  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOf(workspace);
  const provider = providerOf(workspace);
  // A local history has no remote to ask — status only, nothing on the network.
  // A token behind a locked Vault: no fetch, no prompt — "Unlock the Vault to sync".
  const { token, vaultLocked } =
    provider.usesVault || provider.localOnly ? { token: '', vaultLocked: false } : await statusToken(provider, workspace.git);
  if (!provider.localOnly && (token || workspace.git.vaultItemId)) {
    try {
      await invoke('git_fetch', { repoPath: dir, ...credArgs(provider, token, workspace.git), remote: 'origin', branch });
    } catch {
      // offline / transient — fall back to last-known refs
    }
  }
  const status = await invoke('git_status', { repoPath: dir, remote: 'origin', branch });
  return vaultLocked && status ? { ...status, vaultLocked: true } : status;
}

/** Read the mirror's YAML back into an in-memory snapshot (after a pull/merge). */
export async function readMirrorSnapshot(workspace) {
  return readRepoSnapshot(workspace.id);
}

/**
 * Parse a GitHub repo reference into { owner, repo, url }.
 * Accepts "owner/repo", "https://github.com/owner/repo(.git)", or "git@github.com:owner/repo.git".
 */
export function parseGitHubRepo(input) {
  return getProvider('github').parseRepo(input);
}

/**
 * List the linked user's repositories (most recently updated first), for the
 * in-app repo picker. Requires a linked account.
 * @returns {Promise<Array<{fullName,name,owner,private,description,updatedAt,url}>>}
 */
export async function listUserRepos(providerId, accountId) {
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId };
  return provider.listRepos(await requireToken(provider, ctx), ctx);
}

/**
 * The repo picker's catalog for one account (28.10.2026): the user's AND
 * their organisations' repositories, the owners to filter by, and why an
 * organisation's repositories are hidden — `{ repos, owners, notices }`
 * (services/git/repoCatalog.js). A provider without its own catalog answers
 * with its plain list.
 */
export async function listUserRepoCatalog(providerId, accountId) {
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId };
  const token = await requireToken(provider, ctx);
  if (typeof provider.listRepoCatalog === 'function') return provider.listRepoCatalog(token, ctx);
  const list = await provider.listRepos(token, ctx);
  const account = await provider.getAccount(ctx).catch(() => null);
  return catalogFromList(list, { login: account?.login || '', avatarUrl: account?.avatarUrl || '' });
}

/**
 * A repository's branches, for the branch picker: [{ name, protected }].
 * Same account and token as `listUserRepos`; a provider without the ability
 * answers with an empty list (the caller falls back to typing a branch).
 */
export async function listRepoBranches(providerId, accountId, owner, repo) {
  const provider = getProvider(providerId);
  if (!provider.abilities?.listBranches) return [];
  const ctx = { provider: provider.id, accountId };
  return provider.listBranches(await requireToken(provider, ctx), owner, repo, ctx);
}

/**
 * List only repositories flagged as app workspaces (topic "<app>-workspace"),
 * scoped to the linked user.
 */
export async function listWorkspaceRepos(providerId, accountId) {
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId };
  const token = await requireToken(provider, ctx);
  const account = await provider.getAccount(ctx);
  return provider.listWorkspaceRepos(token, account.login, ctx);
}

/**
 * Flag a repo as an app workspace by adding the topic (idempotent, preserves
 * existing topics). Best-effort — never throws.
 */
export async function tagRepoAsWorkspace(owner, repo, providerId, accountId) {
  try {
    const provider = getProvider(providerId);
    if (!provider.abilities.tagWorkspace) return;
    const ctx = { provider: provider.id, accountId };
    await provider.tagWorkspace(await provider.getToken(ctx), owner, repo, ctx);
  } catch {
    // best-effort; tagging failure must not block sync
  }
}

/**
 * Clone a repo into a new workspace's mirror folder and read it back.
 * Uses the linked token when available (required for private repos).
 * @returns {{ branch:string, snapshot:object }}
 */
export async function cloneRepo(workspaceId, { url, provider: providerId, accountId, vaultItemId } = {}) {
  const provider = getProvider(providerId);
  const ctx = { provider: provider.id, accountId, url, vaultItemId };
  const token = provider.usesVault ? '' : await provider.getToken(ctx); // '' when not linked (fine for public repos)
  const dir = await mirrorAbsDir(workspaceId);
  const branch = await invoke('git_clone', {
    repoPath: dir,
    url,
    ...credArgs(provider, token, ctx),
  });
  // Layout-aware: repos flushed by Phase 4 (flat yamlWorkspace layout) parse
  // via loadGitSnapshot; older nested mirrors keep the readMirror path.
  const snapshot = await readRepoSnapshot(workspaceId);
  return { branch: branch || DEFAULT_BRANCH, snapshot };
}
